<template>
  <div class="create-question-deps-panel border rounded-4 bg-white shadow-sm p-3 p-lg-4 w-100">
    <div class="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-2 mb-3">
      <div>
        <div class="text-xs fw-semibold text-dark">Dependent Questions</div>
        <div class="text-xxs text-secondary">Shown only when the trigger condition is met.</div>
      </div>
      <button
        type="button"
        class="btn btn-primary-dark btn-sm rounded-3 text-xxs px-3 py-2"
        @click="add_dependent()"
        :disabled="disabled"
      >
        <i class="bi bi-diagram-3 me-1"></i>
        Add Dependent
      </button>
    </div>

    <div v-if="!questions.length" class="text-secondary text-xxs bg-light rounded-3 px-3 py-2">
      Add dependent questions that only appear when a selected option is chosen.
    </div>

    <div class="create-question-deps-body overflow-auto pe-1">
      <div
        v-for="(dep_question, dep_index) in questions"
        :key="dep_question.id || dep_index"
        class="border-0 rounded-4 p-3 mb-3 bg-white"
      >
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="text-xs fw-semibold text-dark">Dependent Question {{ dep_index + 1 }}</div>
          <button
            type="button"
            class="btn btn-outline-danger btn-sm rounded-3 text-xxs px-3 py-2"
            @click="remove_dependent(dep_index)"
            :disabled="disabled"
          >
            Remove
          </button>
        </div>

        <div class="row g-3 align-items-end">
          <div class="col-12">
            <label class="text-xs form-label fw-semibold mb-2">
              Question Text <span class="text-danger fs-6">*</span>
            </label>
            <input
              v-model="dep_question.question_text"
              type="text"
              class="form-control form-control-sm bg-light border-0 rounded-3 text-xs"
              placeholder="e.g. Which framework do you use?"
              :disabled="disabled"
            />
          </div>

          <div class="col-12">
            <label class="text-xs form-label fw-semibold mb-1">
              Trigger Condition <span class="text-danger fs-6">*</span>
            </label>
            <select
              v-model="dep_question.trigger_condition"
              class="form-select form-select-sm bg-light border-0 rounded-3 text-xs"
              :disabled="disabled || !triggerOptions.length"
            >
              <option disabled value="">
                {{ triggerOptions.length ? 'Select Condition' : 'Add options above first' }}
              </option>
              <option v-for="(cond, cond_index) in triggerOptions" :key="cond_index" :value="cond">
                {{ cond }}
              </option>
            </select>
          </div>

          <div class="col-12">
            <label class="text-xs form-label fw-semibold mb-1">
              Select Question Type <span class="text-danger fs-6">*</span>
            </label>
            <select
              v-model="dep_question.question_type_id"
              class="form-select form-select-sm bg-light border-0 rounded-3 text-xs"
              :disabled="disabled || questionTypesLoading"
              @change="handle_dependent_type_change(dep_question)"
            >
              <option disabled value="">
                {{ questionTypesLoading ? 'Loading Question Types...' : 'Question Type' }}
              </option>
              <option v-for="type in questionTypes" :key="type.id" :value="type.id">
                {{ type.formatted_type || type.label || type.question_type || type.type || '' }}
              </option>
            </select>
          </div>

          <div class="col-12 col-lg-6">
            <label class="text-xs form-label fw-semibold mb-1">Mandatory</label>
            <select
              v-model="dep_question.is_mandatory"
              class="form-select form-select-sm bg-light border-0 rounded-3 text-xs"
              :disabled="disabled"
            >
              <option :value="true">Yes</option>
              <option :value="false">No</option>
            </select>
          </div>

          <div v-if="dep_question.show_option_inputs" class="col-12">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="text-xs form-label fw-semibold mb-0">Options</label>
              <button
                type="button"
                class="btn btn-primary-dark btn-sm rounded-3 text-xxs px-3 py-2"
                @click="add_dependent_option(dep_question)"
                :disabled="disabled"
              >
                <i class="bi bi-plus-lg"></i>
              </button>
            </div>

            <div
              v-for="(option, option_index) in dep_question.options"
              :key="option_index"
              class="mb-3 position-relative"
            >
              <input
                v-model="dep_question.options[option_index].choice"
                type="text"
                class="form-control form-control-sm bg-light border-0 rounded-3 text-xs"
                :placeholder="'Option ' + (option_index + 1)"
                :disabled="disabled"
              />
              <button
                v-if="dep_question.options.length > 2"
                type="button"
                class="btn btn-link text-danger p-0 position-absolute end-0 top-50 translate-middle-y me-3"
                @click="remove_dependent_option(dep_question, option_index)"
                :disabled="disabled"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>
          </div>

          <div v-if="dep_question.show_option_inputs" class="col-12">
            <DependentQuestionsPanel
              v-model="dep_question.dependant_questions"
              :trigger-options="resolve_trigger_options(dep_question)"
              :question-types="questionTypes"
              :question-types-loading="questionTypesLoading"
              :disabled="disabled"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DependentQuestionsPanel',
  props: {
    modelValue: {
      type: Array,
      default: () => [],
    },
    triggerOptions: {
      type: Array,
      default: () => [],
    },
    questionTypes: {
      type: Array,
      default: () => [],
    },
    questionTypesLoading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    questions() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
  },
  methods: {
    extract_choice_label(option) {
      if (option && typeof option === 'object') {
        return String(
          option.choice ??
          option.value ??
          option.option ??
          option.name ??
          option.label ??
          option.text ??
          ''
        ).trim();
      }

      return String(option || '').trim();
    },
    resolve_trigger_options(dep_question) {
      if (!dep_question?.show_option_inputs || !Array.isArray(dep_question?.options)) {
        return [];
      }

      return dep_question.options
        .map((option) => this.extract_choice_label(option))
        .filter(Boolean);
    },
    normalize_type(question_type_id) {
      const selected = this.questionTypes.find((type) => String(type.id) === String(question_type_id)) || {};
      const raw = String(
        selected.question_type ||   // ← move this first, it's the actual raw value
        selected.raw_type ||
        selected.type ||
        selected.formatted_type ||
        selected.label ||
        ''
      ).toLowerCase();

      return raw;
    },
    handle_dependent_type_change(dep_question) {
      const normalized_type = this.normalize_type(dep_question.question_type_id);
      const show_option_inputs =
        normalized_type.includes('single_select') || normalized_type.includes('multi_select');

      dep_question.show_option_inputs = show_option_inputs;

      if (show_option_inputs && (!Array.isArray(dep_question.options) || !dep_question.options.length)) {
        dep_question.options = [{ choice: '' }, { choice: '' }];
      }

      if (!show_option_inputs) {
        dep_question.options = [];
        dep_question.dependant_questions = [];
      }

      if (show_option_inputs && !Array.isArray(dep_question.dependant_questions)) {
        dep_question.dependant_questions = [];
      }
    },
    add_dependent() {
      const next = this.questions.slice();
      next.push({
        id: `${Date.now()}-${next.length}`,
        question_text: '',
        question_description: '',
        question_type_id: '',
        is_mandatory: true,
        question_number: '',
        trigger_condition: '',
        show_option_inputs: false,
        option_inputs: ['', ''],
        options: [],
        dependent_questions: [],
        dependant_questions: [],
      });

      this.$emit('update:modelValue', next);
    },
    remove_dependent(dep_index) {
      const next = this.questions.slice();
      next.splice(dep_index, 1);
      this.$emit('update:modelValue', next);
    },
    add_dependent_option(dep_question) {
      if (!Array.isArray(dep_question.options)) {
        dep_question.options = [];
      }
      dep_question.options.push({ choice: '' });
    },
    remove_dependent_option(dep_question, option_index) {
      if (!Array.isArray(dep_question.options) || dep_question.options.length <= 2) {
        return;
      }

      dep_question.options.splice(option_index, 1);
    },
  },
};
</script>

